/**
 * The page → chat-panel suggestion channel (Issue #13133).
 *
 * A page that knows what its reader might ask hands those questions to the AI
 * chat panel, which shows them on its own empty state instead of the three
 * canned strings it has always shipped ("What needs my attention?"). Pressing
 * one ASKS it — these are questions about the reader's own data, not writing
 * prompts — and the panel reports the press back so the page can count it.
 *
 * ## Why a window event and not a prop or the workspace registry
 *
 * The panel is mounted by the root layout and the page is mounted below it, so
 * there is no prop path between them. The two candidates were this and
 * `WorkspaceContextRegistry` (#12948).
 *
 * The registry is the wrong home: everything published there is SERIALIZED INTO
 * THE PROMPT, and these strings are display copy for a button row the model
 * never needs to read. Putting them there would spend turn context on chrome.
 *
 * So: the same `window` CustomEvent vocabulary the ask bars already use to reach
 * the panel (`ai-chat:open`, `ai-chat:send`, `ai-chat:suggest`) — two more verbs
 * in a dialect that exists rather than a second mechanism beside it.
 *
 * ## The two things a suggestion carries besides its text
 *
 * `id` and `surfaceContext` exist because moving the chips off the page would
 * otherwise have silently dropped both halves of what the ActionBoard's chips
 * did (#13133, Ben):
 *
 *  - `surfaceContext` is the board's own visible text, which grounds the answer.
 *    It travels only with an `ai-chat:send`, so a suggestion that merely filled
 *    the panel's input would produce an ungrounded answer to a question the
 *    board is the whole point of.
 *  - `id` is the ask INTENT (`ASK_INTENTS` in `src/app/actions/actionboard-insights`).
 *    The press happens inside the panel, which knows nothing about either
 *    surface's analytics, so the panel announces it and the page — which owns
 *    its own action — records it. That also restores the `source: 'chip'` signal
 *    Today's ask bar lost when its chip row moved here.
 *
 * ## Lifetime
 *
 * The provider holds the most recent payload and clears it on navigation: the
 * suggestions describe a PAGE, so carrying them to the next one would offer a
 * reader questions about a screen they have left. They deliberately survive
 * "new chat" — starting a fresh conversation does not change what the reader is
 * looking at.
 *
 * Home does NOT use this channel (#13407). Its suggestions are on the page and
 * pressing one calls `sendMessage` with that row's own `surfaceContext`; this
 * channel exists for a page handing questions to the DOCKED panel's empty
 * state, which is not a surface Home has.
 */

import { useEffect } from 'react'

/** The event a page dispatches to hand the panel its suggestions. */
export const AI_CHAT_SUGGESTIONS_EVENT = 'ai-chat:suggestions'

/** The event that puts the reader in the panel's input (#16117). */
export const AI_CHAT_FOCUS_INPUT_EVENT = 'ai-chat:focus-input'

/** The event the panel dispatches back when a reader presses one. */
export const AI_CHAT_SUGGESTION_USED_EVENT = 'ai-chat:suggestion-used'

/**
 * The event a page dispatches to open one specific past conversation (#13391).
 *
 * The original reason for this verb — that a page could not reach the provider
 * at all — is GONE as of #13407: `AiChatProvider` now wraps the application
 * frame in the root layout, so `useAiChat()` from inside a page resolves to
 * the real context rather than the fail-open default. Do not repeat that
 * claim; it was true of the sidecar-slot placement and is not true now.
 *
 * The event stays, on its own merits: what a `Returning` row wants is "open
 * that thread IN THE PANEL", and the provider handles this verb by opening the
 * panel as well as selecting — selecting a conversation nobody can see is not
 * a request anyone makes, and a bare `selectConversation()` call would do only
 * half of it.
 */
export const AI_CHAT_SELECT_CONVERSATION_EVENT = 'ai-chat:select-conversation'

export interface AiChatSelectConversationDetail {
  conversationId: string
}

/** One offered question. `id` is the publisher's own intent key. */
export interface AiChatSuggestion {
  id: string
  question: string
}

/** The kinds of page that publish a context card. */
export type AiChatContextCardKind = 'meeting' | 'note'

/**
 * A block of what the page already knows, shown ABOVE the suggestions (#13813).
 *
 * The suggestions channel carries questions; this carries the answer to "what
 * am I looking at", which is the thing that stops the first message starting
 * cold. Same event, because they are published and cleared together and by
 * the same page — a second event would have two lifetimes to keep in step.
 *
 * DISPLAY COPY ONLY, like the suggestions beside it. None of this is
 * serialized into the prompt: the model's meeting grounding is computed
 * SERVER-SIDE from the session's own org and viewer
 * (`@/lib/ai-chat/meeting-grounding`), precisely so the text a page hands the
 * panel can never become the text a model is told is true.
 *
 * No `testId` travels on the wire. The panel renders the block's
 * `data-testid` as a string literal at its own render site, the way every
 * other testid in `ai-chat-*` is spelled — a hook interpolated from a
 * publisher-supplied field is invisible to `golden-path-e2e-wiring`'s
 * source scan, which reads it as a spec addressing a block nothing renders.
 * It also kept a DOM attribute under the control of whatever dispatched the
 * event, which a `window` CustomEvent detail should never decide.
 */
export interface AiChatContextCard {
  /**
   * What the page is (#14447): the panel captions a note's card as a note
   * and a meeting's as a meeting. Absent reads as `'meeting'`, the only kind
   * there was before notes published one.
   */
  kind?: AiChatContextCardKind
  title: string
  /** Short factual lines — a date, a duration, a count. */
  facts: readonly string[]
  /** The summary paragraph, when there is one. */
  summary?: string | null
  /** Section headings, as labels rather than as bodies. */
  topics?: readonly string[]
}

export interface AiChatSuggestionsDetail {
  suggestions: readonly AiChatSuggestion[]
  /** What the page already knows — see {@link AiChatContextCard}. */
  contextCard?: AiChatContextCard | null
  /**
   * Visible text from the page, handed to the model as grounding with whichever
   * suggestion is pressed. Captured at PUBLISH time, which is when the reader
   * asked to see the suggestions and therefore when the screen they are asking
   * about is the screen in front of them.
   */
  surfaceContext?: string
}

/**
 * Upper bound the provider applies to a received payload. The panel's empty
 * state is a small column, not a menu, and a page that hands over thirty
 * questions has misunderstood the affordance rather than earned a scrollbar.
 */
export const MAX_AI_CHAT_SUGGESTIONS = 6

/**
 * Dispatch suggestions to the panel. Safe to call from any client component;
 * a no-op during SSR. An empty list clears the panel's own.
 *
 * `contextCard` clears with them — it describes the same page, so carrying it
 * past a navigation would caption the panel with a meeting the reader left.
 */
export function publishAiChatSuggestions(
  suggestions: readonly AiChatSuggestion[],
  surfaceContext?: string,
  contextCard?: AiChatContextCard | null,
): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent<AiChatSuggestionsDetail>(AI_CHAT_SUGGESTIONS_EVENT, {
      detail: { suggestions, surfaceContext, contextCard: contextCard ?? null },
    }),
  )
}

/** Announce that a reader pressed one, for the publishing page to count. */
export function notifyAiChatSuggestionUsed(suggestion: AiChatSuggestion): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent<AiChatSuggestion>(AI_CHAT_SUGGESTION_USED_EVENT, {
      detail: suggestion,
    }),
  )
}

/**
 * Subscribe to presses of the suggestions THIS page published.
 *
 * The handler is held in a ref-free closure on purpose — the effect re-subscribes
 * when `handler` changes, so callers pass a `useCallback`.
 *
 * `ownedIds` is what makes "THIS page" true rather than aspirational (#13133
 * review). The event is a window broadcast, so every mounted subscriber hears
 * every press; the header used to claim a page "simply never hears an id it
 * recognizes", which described a filter that did not exist. Today the two ask
 * bars are route-exclusive so nothing mis-attributes, but the moment a third
 * publisher ships — or two ask surfaces coexist on one route — both subscribers
 * would record the same press, and a silent double-count is precisely what this
 * channel exists to prevent: it was built to keep `source: 'chip'` separable
 * from free-form at all.
 *
 * Pass the ids the caller published. An id the caller does not own is dropped
 * before the handler sees it.
 */
export function useAiChatSuggestionUsed(
  handler: (suggestion: AiChatSuggestion) => void,
  ownedIds: readonly string[],
): void {
  // Serialized, so a caller composing `ids` inline does not re-subscribe on
  // every render — the same reason the provider depends on a query STRING
  // rather than on a `ReadonlyURLSearchParams` instance.
  const ownedKey = ownedIds.join('\u0000')

  useEffect(() => {
    const owned = new Set(ownedKey.length > 0 ? ownedKey.split('\u0000') : [])
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<AiChatSuggestion>).detail
      if (!detail || typeof detail.question !== 'string') return
      if (!owned.has(detail.id)) return
      handler(detail)
    }
    window.addEventListener(AI_CHAT_SUGGESTION_USED_EVENT, listener)
    return () =>
      window.removeEventListener(AI_CHAT_SUGGESTION_USED_EVENT, listener)
  }, [handler, ownedKey])
}
